// MACHINERY V2 (Stroje) — the tab-toolbar select (M2.5).
//
// Reference impl: design_handoff_machinery_v3/machinery-profile-parts.jsx
// (`PcSelect`) — a 32px control with a mono uppercase eyebrow, the current value
// and a chevron; artboards 1009 (desktop) and 1013 (phone).
//
// RADIX, NOT `<select>` AND NOT MUI. §3 pins the v2 primitive library to Radix;
// a native `<select>` cannot carry the eyebrow-inside-the-control layout the
// artboard draws, and MUI is out.
//
// PORTAL RULE (MachineryV2Layout.tsx:20-39): DropdownMenu.Content portals to
// document.body, outside `.machinery-v2-root`, where the scoped vars do not
// resolve. Content re-carries the scope classes, sets a transparent background,
// paints on the inner `.ms-menu` div and reads `palette` LIVE on every render.
// Same countermeasure as chrome/ChromeRightMenus.tsx `MenuContent` (canonical)
// and fleet/FleetCardMenu.tsx.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";

import { useAppSelector } from "../../../../../shared/hooks/sharedHooks";

export interface TabSelectOption {
  id: string;
  name: string;
}

export interface TabSelectProps {
  label: string;
  /**
   * The "no filter" row label, e.g. common:all.
   *
   * M2.8 made it OPTIONAL. Every M2.5 caller is a FILTER, where "any" is a real
   * choice, and they all still pass it — unchanged. The catalog's page-size
   * picker is not a filter: 20/50/100 are the only values and there is always
   * one selected, so an "any" row would be a choice that means nothing. Omit it
   * and the row is not rendered.
   */
  anyLabel?: string;
  value?: string;
  options: TabSelectOption[];
  onChange: (value?: string) => void;
  /**
   * Board 24 (M2.16d): fill the grid cell instead of sitting at its natural
   * 140-220px. On a 390px phone the desktop `minWidth: 140` plus `maxWidth: 220`
   * leaves the two facet selects ragged inside an equal-width 2-up row, and the
   * 140 minimum can overflow a half-column on the narrowest devices.
   *
   * OMITTED IS THE DESKTOP CONTROL, UNCHANGED. Its 44px touch floor arrives
   * from the module-wide below-1280 block in mobile/machinery-v2-touch.scss,
   * which matches the `aria-expanded` Radix sets on this trigger. That floor
   * was scoped to the profile page until M2.16e root-scoped it, so this
   * trigger is now raised on CatalogPagination too — the same 32px bordered
   * rectangle growing to 44, which is the sanctioned treatment for a control
   * whose paint is not artboard-fixed.
   *
   * >>> WHEN `fill` IS SET, THE WIDTH HALF OF THAT FLOOR IS DECLARED HERE AND
   * NOT INHERITED (M2.16d review, F4). <<< The first draft wrote
   * `minWidth: fill ? 0 : 140`, and an INLINE `min-width: 0` outranks the
   * stylesheet's `min-width: 44px` — so the exact control the prop was added
   * for was the one control the floor never reached. Nothing broke visibly
   * only because the co-located `width: "100%"` happened to keep the trigger
   * wide, which makes the guarantee ACCIDENTAL rather than stated. `fill` is
   * only ever passed on the phone tier, which is entirely below 1280, so 44 is
   * the correct minimum at every width this branch renders at — and it is far
   * below the half-column widths the prop exists to fit inside (~187px at
   * 390px, ~148px at 320px), so it cannot reintroduce the overflow above.
   */
  fill?: boolean;
}

const TabSelect: React.FC<TabSelectProps> = ({
  label,
  anyLabel,
  value,
  options,
  onChange,
  fill,
}) => {
  const palette = useAppSelector((s) => s.machineryV2Ui.palette);
  const selected = options.find((o) => o.id === value);

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={label}
          style={{
            height: 32,
            padding: "0 10px",
            borderRadius: 7,
            // 44, NEVER 0 — see the `fill` docblock. An inline `minWidth: 0`
            // here silently voided the stylesheet's below-1280 floor.
            minWidth: fill ? 44 : 140,
            maxWidth: fill ? "100%" : 220,
            width: fill ? "100%" : undefined,
            background: "var(--card)",
            border: "1px solid var(--border)",
            display: "flex",
            alignItems: "center",
            gap: 8,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 8.5,
              letterSpacing: "0.07em",
              textTransform: "uppercase",
              color: "var(--faint)",
              flexShrink: 0,
            }}
          >
            {label}
          </span>
          <span
            style={{
              fontSize: 12.5,
              color: "var(--ink-2)",
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {selected ? selected.name : anyLabel || ""}
          </span>
          <svg
            viewBox="0 0 12 12"
            width="11"
            height="11"
            fill="none"
            stroke="var(--muted)"
            strokeWidth="1.5"
            strokeLinecap="round"
            style={{ marginLeft: "auto", flexShrink: 0 }}
            aria-hidden
          >
            <path d="M3 4.5 L6 7.5 L9 4.5" />
          </svg>
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className={`machinery-v2-root p-${palette} mod-stroje`}
          align="start"
          sideOffset={6}
          onClick={(e) => e.stopPropagation()}
          style={{ background: "transparent", zIndex: 1290 }}
        >
          <div
            className="ms-menu"
            role="none"
            style={{ width: 240, maxHeight: 320, overflowY: "auto" }}
          >
            {anyLabel !== undefined && (
              <DropdownMenu.Item className="mi" onSelect={() => onChange(undefined)}>
                <span>{anyLabel}</span>
              </DropdownMenu.Item>
            )}
            {options.map((o) => (
              <DropdownMenu.Item
                key={o.id}
                className="mi"
                onSelect={() => onChange(o.id)}
                style={o.id === value ? { color: "var(--mod-accent)", fontWeight: 600 } : undefined}
              >
                <span>{o.name}</span>
              </DropdownMenu.Item>
            ))}
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default TabSelect;
